import { Box, CircularProgress, Portal } from '@mui/material';
import successBackground from 'assets/Background.png';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Message as ApiMessage } from 'services/chat';
import { useUser } from 'services/users';
import { safeBase64Encode } from '@utils/util.functions';
import { monotonicFactory } from 'ulidx';
import { useChannel } from '../hooks/useChat';
import { useChatStore, useMessageStore } from '../store';
import { Message, MessageSendStatus } from '../types';
import { MessageContent } from '../types/messageContent';
import { ChatNotSelectedWindow } from './ChatNotSelectedWindow';
import { ChatWindowHeader } from './ChatWindowHeader';
import { MessageBubble } from './MessageBubble';
import { MessageInput } from './MessageInput';
import { MessageSearchHeader } from './MessageSearchHeader';

const ulid = monotonicFactory();

// Add throttle utility for scroll handling
const throttle = (func: Function, limit: number) => {
	let inThrottle: boolean;
	return function (this: any, ...args: any[]) {
		if (!inThrottle) {
			func.apply(this, args);
			inThrottle = true;
			setTimeout(() => (inThrottle = false), limit);
		}
	};
};

// Add debounce utility for load more messages
const debounce = (func: Function, delay: number) => {
	let timeoutId: NodeJS.Timeout;
	return function (this: any, ...args: any[]) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => func.apply(this, args), delay);
	};
};

const convertApiMessageToLocalModel = (apiMessage: ApiMessage): Message => {
	let messageContents: MessageContent[] = [];
	try {
		if (apiMessage.content) {
			const contentArray = JSON.parse(apiMessage.content);
			if (Array.isArray(contentArray)) {
				messageContents = contentArray;
			}
		}
	} catch (error) {
		console.error('Error parsing message content:', error);
	}

	const message = new Message(
		apiMessage.id,
		apiMessage.author.id,
		apiMessage.channelId,
		messageContents,
		apiMessage.createdAt,
		apiMessage.author,
		MessageSendStatus.Sent,
		apiMessage.status,
		apiMessage.repliedMessageId
	);

	// Set attachments if they exist in the API response
	if (
		apiMessage.attachments &&
		Array.isArray(apiMessage.attachments) &&
		apiMessage.attachments.length > 0
	) {
		// Fix attachments fileType if needed
		const fixedAttachments = apiMessage.attachments.map((attachment) => {
			// If fileType is just "image" instead of "image/jpeg", fix it
			if (attachment.fileType === 'image') {
				// Add null check before accessing fileName
				if (attachment.fileName) {
					const fileExtension = attachment.fileName.split('.').pop()?.toLowerCase();
					let mimeType = 'image/jpeg'; // Default MIME type

					// Set more specific MIME type based on extension
					if (fileExtension === 'png') mimeType = 'image/png';
					if (fileExtension === 'gif') mimeType = 'image/gif';
					if (fileExtension === 'webp') mimeType = 'image/webp';

					// Add support for audio files
					if (fileExtension === 'mp3') mimeType = 'audio/mpeg';
					if (fileExtension === 'wav') mimeType = 'audio/wav';
					if (fileExtension === 'ogg') mimeType = 'audio/ogg';
					if (fileExtension === 'm4a') mimeType = 'audio/mp4';
					if (fileExtension === 'aac') mimeType = 'audio/aac';
					if (fileExtension === 'webm') mimeType = 'audio/webm';

					return {
						...attachment,
						fileType: mimeType,
					};
				}
			}
			// If fileType is just "audio" instead of "audio/mpeg" etc., fix it
			else if (attachment.fileType === 'audio') {
				if (attachment.fileName) {
					const fileExtension = attachment.fileName.split('.').pop()?.toLowerCase();
					let mimeType = 'audio/mpeg'; // Default MIME type for audio

					// Set more specific MIME type based on extension
					if (fileExtension === 'wav') mimeType = 'audio/wav';
					if (fileExtension === 'ogg') mimeType = 'audio/ogg';
					if (fileExtension === 'm4a') mimeType = 'audio/mp4';
					if (fileExtension === 'aac') mimeType = 'audio/aac';
					if (fileExtension === 'webm') mimeType = 'audio/webm';

					return {
						...attachment,
						fileType: mimeType,
					};
				}
			}
			return attachment;
		});

		message.setAttachments(fixedAttachments);
	}

	return message;
};

export const ChatWindow: React.FC = () => {
	const { t } = useTranslation();
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
	const [editingMessage, setEditingMessage] = useState<Message | null>(null);
	const [isManualLoading, setIsManualLoading] = useState(false);
	const [searchHeaderRef, setSearchHeaderRef] = useState<HTMLElement | null>(null);
	const [showSearchHint, setShowSearchHint] = useState(false);
	const [hintPosition, setHintPosition] = useState({ top: 0, left: 0 });

	// Scroll position tracking for better scroll restoration
	const scrollPositionRef = useRef({ scrollTop: 0, scrollHeight: 0 });
	const isScrollingToBottomRef = useRef(false);
	const lastMessageCountRef = useRef(0);

	// Simplified state management for load more functionality
	const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
	const lastScrollHeightRef = useRef(0);
	const lastLoadMoreTriggerRef = useRef(0);

	// Get state from stores
	const { 
		selectedChannelId, 
		isMessageSearchMode, 
		messageSearchQuery,
		setMessageSearchMode,
		clearMessageSearch 
	} = useChatStore();
	const { data: user } = useUser();

	// Use pure Zustand store directly
	const { channel, error: channelError } = useChannel(selectedChannelId || '');
	const {
		getChannelMessages,
		fetchMessages,
		loadMoreMessages,
		editMessage: zustandEditMessage,
		deleteMessageFromAPI,
		loadingStates,
		setEditorDraft,
		getPendingMessages,
		searchMessages,
		clearSearchResults,
		getSearchResults,
		searchLoadingStates,
	} = useMessageStore();

	// Get messages from Zustand store - either search results or regular messages
	const regularMessages = selectedChannelId ? getChannelMessages(selectedChannelId) : [];
	const searchResults = selectedChannelId ? getSearchResults(selectedChannelId) : [];
	const searchState = selectedChannelId ? searchLoadingStates[selectedChannelId] : null;
	
	// Use search results when in search mode and have query, otherwise use regular messages
	const allMessages = isMessageSearchMode && messageSearchQuery.trim().length >= 3 
		? searchResults 
		: regularMessages;
		
	const loadingState = selectedChannelId ? loadingStates[selectedChannelId] : null;
	const isLoading = loadingState?.isLoading || false;
	const isLoadingMore = loadingState?.isLoadingMore || false;
	const isReachingEnd = loadingState?.isReachingEnd || false;
	const messagesError = loadingState?.error || null;
	
	// Search-specific loading states
	const isSearching = searchState?.isSearching || false;
	const isLoadingMoreSearchResults = searchState?.isLoadingMoreResults || false;
	const isSearchReachingEnd = searchState?.isReachingEnd || false;
	const searchError = searchState?.error || null;

	// Handle case where selected channel isn't loaded yet
	useEffect(() => {
		if (selectedChannelId && channelError && !channel) {
			console.error('Error loading channel:', channelError);
		}
	}, [selectedChannelId, channel, channelError]);

	// Handle search hint with 1-second delay and positioning
	useEffect(() => {
		const shouldShow = isMessageSearchMode && 
			messageSearchQuery.trim().length > 0 && 
			messageSearchQuery.trim().length < 3;

		if (shouldShow && searchHeaderRef) {
			const timer = setTimeout(() => {
				const rect = searchHeaderRef.getBoundingClientRect();
				setHintPosition({
					top: rect.bottom + 8,
					left: rect.left + rect.width / 2,
				});
				setShowSearchHint(true);
			}, 1000);
			return () => clearTimeout(timer);
		} else {
			setShowSearchHint(false);
		}
	}, [isMessageSearchMode, messageSearchQuery, searchHeaderRef]);

	// Fetch messages when channel changes using Zustand
	useEffect(() => {
		if (selectedChannelId) {
			setIsManualLoading(true);
			// Reset scroll position flags for new channel
			setShouldScrollToBottom(true);
			lastMessageCountRef.current = 0; // Reset message count tracking
			
			fetchMessages(selectedChannelId, { limit: 20, order: 'desc' });
			const timer = setTimeout(() => {
				setIsManualLoading(false);
			}, 1500); // Show loading for at least 1.5 seconds
			return () => clearTimeout(timer);
		}
	}, [selectedChannelId, fetchMessages]);



	// Log messages error if it exists
	if (messagesError) {
		console.error('Error fetching messages:', messagesError);
	}
	
	// Log search error if it exists
	if (searchError) {
		console.error('Error searching messages:', searchError);
	}

	// Memoize message deduplication and conversion to prevent unnecessary re-renders
	const combinedMessages = useMemo(() => {
		if (!allMessages) return [];
		
		// Create a Map for O(1) deduplication instead of array.findIndex
		const messageMap = new Map();
		allMessages.forEach(message => {
			if (!messageMap.has(message.id)) {
				messageMap.set(message.id, convertApiMessageToLocalModel(message));
			}
		});
		
		return Array.from(messageMap.values());
	}, [allMessages]);

	// Get pending messages from Zustand store
	const pendingMessages = selectedChannelId ? getPendingMessages(selectedChannelId) : [];

	// Combine pending messages with confirmed messages, avoiding duplicates
	const allMessagesWithPending = useMemo(() => {
		const messageMap = new Map();

		// Add confirmed messages first
		combinedMessages.forEach((message) => {
			messageMap.set(message.id, message);
		});

		// Add pending messages only if they don't already exist as confirmed
		pendingMessages.forEach((message) => {
			if (!messageMap.has(message.id)) {
				messageMap.set(message.id, message);
			}
		});

		return Array.from(messageMap.values());
	}, [combinedMessages, pendingMessages]);

	// Memoize sorted messages to prevent re-sorting on every render
	// This prevents edited messages from moving positions unnecessarily
	const sortedMessagesWithPending = useMemo(() => {
		if (!allMessagesWithPending) return [];
		
		// Sort messages only when the message array actually changes
		// This prevents re-sorting on every render which can move edited messages
		const sortedMessages = [...allMessagesWithPending].sort((a, b) => {
			// Sort by creation date for correct message order
			const dateA =
				typeof a.createDate === 'number'
					? a.createDate
					: new Date(a.createDate).getTime() / 1000;
			const dateB =
				typeof b.createDate === 'number'
					? b.createDate
					: new Date(b.createDate).getTime() / 1000;
			return dateA - dateB;
		});

		return sortedMessages;
	}, [allMessagesWithPending]);

	// Debug logging for pure Zustand approach - only log when there are issues
	useEffect(() => {
		if (selectedChannelId && messagesError) {
			console.error('ChatWindow Error:', {
				selectedChannelId,
				error: messagesError,
			});
		}
	}, [selectedChannelId, messagesError]);

	// Function to check if user is scrolled to bottom
	const isScrolledToBottom = useCallback(() => {
		const container = messagesContainerRef.current;
		if (!container) return true;

		// Check if the user is near the bottom (within 100px)
		const threshold = 100;
		return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
	}, []);

	// Function to check if user is near the top
	const isScrolledNearTop = useCallback(() => {
		const container = messagesContainerRef.current;
		if (!container) return false;

		// More generous threshold for better detection
		const threshold = 200; // Consider "near top" if within 200px of the top
		const scrollTop = container.scrollTop;

		// Also consider if we're very close to the top relative to total height
		const totalHeight = container.scrollHeight;
		const visibleHeight = container.clientHeight;
		const scrollPercentage = scrollTop / Math.max(1, totalHeight - visibleHeight);

		return scrollTop <= threshold || scrollPercentage <= 0.05; // Within 200px or top 5%
	}, []);

	// Improved scroll to bottom function with better timing
	const scrollToBottom = useCallback(() => {
		const container = messagesContainerRef.current;
		if (!container || isScrollingToBottomRef.current) return;

		isScrollingToBottomRef.current = true;
		
		// Use requestAnimationFrame for smooth scrolling
		requestAnimationFrame(() => {
			if (container) {
				container.scrollTop = container.scrollHeight;
				// Update scroll position tracking
				scrollPositionRef.current = {
					scrollTop: container.scrollTop,
					scrollHeight: container.scrollHeight
				};
			}
			isScrollingToBottomRef.current = false;
		});
	}, []);

	// Improved load more messages function with better scroll position restoration
	const handleLoadMoreMessages = useCallback(async () => {
		if (!selectedChannelId || isLoadingMoreMessages) {
			return;
		}

		// Check different conditions based on search mode
		const inSearchMode = isMessageSearchMode && messageSearchQuery.trim().length >= 3;
		if (inSearchMode) {
			// For search mode, check search-specific loading states
			if (isSearching || isLoadingMoreSearchResults || isSearchReachingEnd) {
				return;
			}
		} else {
			// For regular mode, check regular loading states
			if (isLoading || isLoadingMore || isReachingEnd) {
				return;
			}
		}

		const container = messagesContainerRef.current;
		if (!container) return;

		// Store detailed scroll position before loading
		const scrollMetrics = {
			scrollTop: container.scrollTop,
			scrollHeight: container.scrollHeight,
			clientHeight: container.clientHeight,
		};

		// Temporarily disable auto-scroll during load more
		const wasScrolledToBottom = shouldScrollToBottom;
		setShouldScrollToBottom(false);

		setIsLoadingMoreMessages(true);

		try {
			if (inSearchMode) {
				// Load more search results
				const { loadMoreSearchResults } = useMessageStore.getState();
				await loadMoreSearchResults(selectedChannelId, messageSearchQuery.trim());
			} else {
				// Load more regular messages
				await loadMoreMessages(selectedChannelId);
			}

			// Wait for DOM updates and then restore scroll position
			// Use multiple animation frames to ensure all rendering is complete
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (!container) return;
					
					const newScrollHeight = container.scrollHeight;
					const heightDifference = newScrollHeight - scrollMetrics.scrollHeight;

					if (heightDifference > 0) {
						// When loading messages at the top, maintain the same content in view
						// by adding the height difference to the current scroll position
						const newScrollTop = scrollMetrics.scrollTop + heightDifference;
						container.scrollTop = Math.max(0, newScrollTop);
						
						// Update position tracking
						scrollPositionRef.current = {
							scrollTop: container.scrollTop,
							scrollHeight: container.scrollHeight
						};
						
						// Verify the position was set correctly and adjust if needed
						setTimeout(() => {
							if (container && Math.abs(container.scrollTop - newScrollTop) > 1) {
								container.scrollTop = newScrollTop;
								scrollPositionRef.current = {
									scrollTop: container.scrollTop,
									scrollHeight: container.scrollHeight
								};
							}
						}, 50);
					}
				});
			});

		} catch (error) {
			console.error('Error loading more messages:', error);
		} finally {
			// Reset loading state and restore auto-scroll behavior
			setTimeout(() => {
				setIsLoadingMoreMessages(false);
				// Only restore auto-scroll if user was originally at bottom
				if (wasScrolledToBottom) {
					setShouldScrollToBottom(true);
				}
			}, 200);
		}
	}, [
		selectedChannelId,
		isLoading,
		isLoadingMore,
		isReachingEnd,
		isLoadingMoreMessages,
		isMessageSearchMode,
		messageSearchQuery,
		isSearching,
		isLoadingMoreSearchResults,
		isSearchReachingEnd,
		loadMoreMessages,
		shouldScrollToBottom,
	]);

	// Improved scroll handler with better debouncing
	const handleScroll = useCallback(() => {
		const container = messagesContainerRef.current;
		if (!container || isScrollingToBottomRef.current) return;

		// Update scroll position tracking
		scrollPositionRef.current = {
			scrollTop: container.scrollTop,
			scrollHeight: container.scrollHeight
		};

		// Update scroll to bottom flag
		setShouldScrollToBottom(isScrolledToBottom());

		// Check if we need to load more messages
		const isNearTop = isScrolledNearTop();
		const now = Date.now();

		// Determine loading states based on search mode
		const inSearchMode = isMessageSearchMode && messageSearchQuery.trim().length >= 3;
		const currentlyLoading = inSearchMode ? isSearching : isLoading;
		const currentlyLoadingMore = inSearchMode ? isLoadingMoreSearchResults : isLoadingMore;
		const currentlyReachingEnd = inSearchMode ? isSearchReachingEnd : isReachingEnd;

		// Only trigger load more if conditions are met
		if (
			isNearTop &&
			now - lastLoadMoreTriggerRef.current > 1000 && // 1 second delay between triggers
			!isLoadingMoreMessages &&
			!currentlyLoadingMore &&
			!currentlyLoading &&
			!currentlyReachingEnd
		) {
			console.log('🔄 Triggering load more messages from scroll');
			lastLoadMoreTriggerRef.current = now;
			handleLoadMoreMessages();
		}
	}, [
		isScrolledToBottom,
		isScrolledNearTop,
		handleLoadMoreMessages,
		isLoadingMoreMessages,
		isLoadingMore,
		isLoading,
		isReachingEnd,
		isMessageSearchMode,
		messageSearchQuery,
		isSearching,
		isLoadingMoreSearchResults,
		isSearchReachingEnd,
	]);

	// Throttled scroll handler with optimized delay
	const throttledScrollHandler = useCallback(throttle(handleScroll, 100), [handleScroll]);

	// Handle scroll events
	useEffect(() => {
		const container = messagesContainerRef.current;
		if (container) {
			container.addEventListener('scroll', throttledScrollHandler, { passive: true });
			return () => container.removeEventListener('scroll', throttledScrollHandler);
		}
	}, [throttledScrollHandler]);

	// Scroll to bottom when channel changes and messages are loaded
	useEffect(() => {
		if (selectedChannelId && !isLoading && !isManualLoading && allMessagesWithPending.length > 0) {
			// Small delay to ensure messages are rendered
			setTimeout(() => {
				scrollToBottom();
			}, 100);
		}
	}, [selectedChannelId, isLoading, isManualLoading, allMessagesWithPending.length > 0, scrollToBottom]);

	// Smart auto-scroll effect with better conditions
	useEffect(() => {
		const container = messagesContainerRef.current;
		if (!container) return;

		const currentMessageCount = allMessagesWithPending.length;
		const messageCountChanged = currentMessageCount !== lastMessageCountRef.current;
		lastMessageCountRef.current = currentMessageCount;

		// Only scroll to bottom if:
		// 1. We should scroll to bottom (user was at bottom)
		// 2. Not manually loading
		// 3. Not loading more messages
		// 4. Message count actually changed (new message arrived)
		if (
			shouldScrollToBottom && 
			!isManualLoading && 
			!isLoading && 
			!isLoadingMoreMessages &&
			messageCountChanged
		) {
			// Small delay to ensure DOM is updated
			setTimeout(scrollToBottom, 10);
		}
	}, [
		sortedMessagesWithPending.length, // Only depend on length to reduce re-renders
		shouldScrollToBottom,
		isManualLoading,
		isLoading,
		isLoadingMoreMessages,
		scrollToBottom,
	]);

	// If no channel is selected, show the "no channel selected" view
	if (!selectedChannelId || !channel) {
		return <ChatNotSelectedWindow successBackground={successBackground} />;
	}

	// Handle UI updates when message is sent (MessageInput handles the actual sending)
	const handleMessageSent = (message: Message) => {
		if (!selectedChannelId) return;

		// Check if user is at bottom before sending
		const wasAtBottom = isScrolledToBottom();

		// Clear draft (MessageInput already clears its own draft, this is for any other drafts)
		setEditorDraft(selectedChannelId, '');

		// If user was at bottom, ensure we scroll down after message is sent
		if (wasAtBottom) {
			setShouldScrollToBottom(true);
			// Shorter timeout for better UX
			setTimeout(scrollToBottom, 10);
		}
	};

	const handleDeleteMessage = async (message: Message) => {
		if (!selectedChannelId) return;
		try {
			await deleteMessageFromAPI(selectedChannelId, message.id);
		} catch (error) {
			console.error('Error deleting message:', error);
		}
	};

	const handleRetryMessage = async (failedMessage: Message) => {
		if (!selectedChannelId || !user) return;

		// Create new message with new ID for retry
		const newId = ulid(Date.now());
		const retryMessage = new Message(
			newId,
			failedMessage.author,
			failedMessage.channelId,
			failedMessage.contents,
			Math.floor(Date.now() / 1000),
			failedMessage.authorDto,
			MessageSendStatus.Sending,
			failedMessage.messageStatus,
			failedMessage.replyTo
		);

		// Copy attachments
		if (failedMessage.attachments) {
			retryMessage.setAttachments(failedMessage.attachments);
		}

		// Add to pending messages using Zustand store
		const {
			addPendingMessage,
			sendMessage: zustandSendMessage,
			updatePendingMessage,
		} = useMessageStore.getState();
		addPendingMessage(selectedChannelId, retryMessage);

		// Prepare payload for sending
		const attachmentIds =
			retryMessage.attachments?.filter((a) => a && a.s3_key).map((a) => a.s3_key || '') || [];

		// Use safe UTF-8 encoding for message content (handles Unicode characters)
		const messageContentStr = JSON.stringify(retryMessage.contents || []);
		const encodedContent = safeBase64Encode(messageContentStr);

		const payload = {
			messageId: retryMessage.id,
			content: encodedContent,
			attachmentIds: attachmentIds,
		};

		// Send the message
		try {
			const result = await zustandSendMessage(selectedChannelId, payload);

			if (!result) {
				updatePendingMessage(selectedChannelId, retryMessage.id, MessageSendStatus.Failed);
			}
			// If successful, WebSocket will handle removing the pending message
		} catch (error) {
			console.error('Retry send failed:', error);
			updatePendingMessage(selectedChannelId, retryMessage.id, MessageSendStatus.Failed);
		}

		// Handle UI updates (scrolling, etc.)
		handleMessageSent(retryMessage);
	};

	// Add the handleEditMessage function
	const handleEditMessage = async (messageId: string, contents: any[], attachmentIds: string[]) => {
		if (selectedChannelId) {
			try {
				// Use safe UTF-8 encoding for edit message content (handles Unicode characters)
				const contentStr = JSON.stringify(contents);
				const encodedContent = safeBase64Encode(contentStr);

				await zustandEditMessage(selectedChannelId, messageId, {
					content: encodedContent,
					attachmentIds,
				});

				setEditingMessage(null);
			} catch (error) {
				console.error('Error editing message:', error);
				setEditingMessage(null);
			}
		}
	};

	// Search functionality handlers
	const handleSearchClick = () => {
		setMessageSearchMode(true);
	};

	const handleSearchClose = () => {
		if (selectedChannelId) {
			clearSearchResults(selectedChannelId);
		}
		clearMessageSearch();
	};

	const handleSearch = async (query: string) => {
		if (selectedChannelId && query.trim().length >= 3) {
			try {
				await searchMessages(selectedChannelId, query.trim());
			} catch (error) {
				console.error('Error searching messages:', error);
			}
		}
	};

	const handleMessageClick = (messageId: string) => {
		// Exit search mode and scroll to the message
		handleSearchClose();
		// TODO: Implement scroll to specific message functionality
		console.log('Navigate to message:', messageId);
	};

	// Simplified manual load more handler for potential UI button
	const handleLoadMore = async () => {
		if (
			!selectedChannelId ||
			isLoading ||
			isLoadingMore ||
			isReachingEnd ||
			isLoadingMoreMessages
		) {
			return;
		}

		await handleLoadMoreMessages();
	};

	return (
		<Box
			flex={1}
			display="flex"
			height="100%"
			maxHeight="100vh"
			flexDirection="column"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				bgcolor: 'background.paper',
				position: 'relative',
			}}
		>
			{/* Chat header with channel info or search header */}
			{isMessageSearchMode ? (
				<MessageSearchHeader 
					onSearch={handleSearch}
					onClose={handleSearchClose}
					resultCount={messageSearchQuery.trim().length >= 3 ? searchResults.length : undefined}
					onSearchInputRef={setSearchHeaderRef}
				/>
			) : (
				<ChatWindowHeader onSearchClick={handleSearchClick} />
			)}

			{/* Messages container - shows both regular and search results */}
			<Box
				ref={messagesContainerRef}
				sx={{
					flexGrow: 1,
					overflow: 'auto',
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					p: 2,
					backgroundImage: `url(${successBackground})`,
					backgroundSize: 'cover',
					backgroundRepeat: 'no-repeat',
					position: 'relative',
					// Add will-change to optimize scrolling performance
					willChange: 'transform',
				}}
			>
				{(isLoading || isManualLoading || (isMessageSearchMode && isSearching && searchResults.length === 0)) && (
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							background: 'rgba(255, 255, 255, 0.8)',
							zIndex: 10,
						}}
					>
						<CircularProgress color="primary" size={40} thickness={2} />
						<Box mt={2} fontWeight="bold" fontSize="18px">
							{isMessageSearchMode && isSearching 
								? t('chats.searchingMessages', 'Searching messages...')
								: t('chats.loadingMessages')
							}
						</Box>
					</Box>
				)}

				{/* Show load more indicator at the top */}
				{(isLoadingMore || isLoadingMoreMessages || isLoadingMoreSearchResults) && !isLoading && !isManualLoading && !(isMessageSearchMode && isSearching && searchResults.length === 0) && (
					<Box
						sx={{
							textAlign: 'center',
							py: 2,
							color: 'primary.main',
							fontSize: '14px',
							fontWeight: 'medium',
							bgcolor: 'rgba(255, 255, 255, 0.9)',
							borderRadius: 1,
							mb: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1,
						}}
					>
						<CircularProgress size={16} thickness={4} />
						{isMessageSearchMode && isLoadingMoreSearchResults
							? t('chats.loadingMoreResults', 'Loading more results...')
							: t('chats.loadingMoreMessages')
						}
					</Box>
				)}



				{/* Show no search results message */}
				{isMessageSearchMode && 
					messageSearchQuery.trim().length >= 3 && 
					!isSearching && 
					searchResults.length === 0 && (
					<Box
						sx={{
							textAlign: 'center',
							py: 4,
							color: 'text.secondary',
							fontSize: '14px',
							fontWeight: 'medium',
							bgcolor: 'rgba(255, 255, 255, 0.7)',
							borderRadius: 1,
							mb: 1,
							mx: 2,
						}}
					>
						<Box sx={{ fontWeight: 'bold', mb: 1 }}>
							{t('chats.noSearchResults', 'No messages found')}
						</Box>
						<Box>
							{t('chats.noSearchResultsDescription', 'Try different keywords or check your spelling')}
						</Box>
					</Box>
				)}

				{/* Show search error */}
				{isMessageSearchMode && searchError && (
					<Box
						sx={{
							textAlign: 'center',
							py: 4,
							color: 'error.main',
							fontSize: '14px',
							fontWeight: 'medium',
							bgcolor: 'rgba(255, 0, 0, 0.1)',
							borderRadius: 1,
							mb: 1,
							mx: 2,
						}}
					>
						<Box sx={{ fontWeight: 'bold', mb: 1 }}>
							{t('chats.searchError', 'Search Error')}
						</Box>
						<Box>
							{searchError}
						</Box>
					</Box>
				)}

				{/* Display combined messages */}
				{sortedMessagesWithPending.map((message) => {
					const isCurrentUser = message.author === user?.user_id;

					return (
						<MessageBubble
							key={message.id}
							message={message}
							isCurrentUser={isCurrentUser}
							onEdit={setEditingMessage}
							onDelete={handleDeleteMessage}
							onRetry={handleRetryMessage}
						/>
					);
				})}
			</Box>

			{/* Message input - always visible */}
			<MessageInput
				channelId={selectedChannelId}
				currentUser={user!}
				onMessageSent={handleMessageSent}
				editingMessage={editingMessage}
				onCancelEdit={() => {
					setEditingMessage(null);
				}}
				onSaveEdit={handleEditMessage}
			/>

			{/* Search instructions tooltip */}
			{showSearchHint && (
				<Portal>
					<Box
						sx={{
							position: 'fixed',
							top: hintPosition.top,
							left: hintPosition.left,
							transform: 'translateX(-50%)',
							backgroundColor: 'rgba(0, 0, 0, 0.85)',
							color: 'white',
							padding: '6px 10px',
							borderRadius: '6px',
							fontSize: '11px',
							fontWeight: '500',
							zIndex: 1400,
							pointerEvents: 'none',
							maxWidth: '180px',
							textAlign: 'center',
							boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
							'&::before': {
								content: '""',
								position: 'absolute',
								top: '-3px',
								left: '50%',
								transform: 'translateX(-50%)',
								width: 0,
								height: 0,
								borderLeft: '3px solid transparent',
								borderRight: '3px solid transparent',
								borderBottom: '3px solid rgba(0, 0, 0, 0.85)',
							},
						}}
					>
						{t('chats.searchInstructions', 'Type at least 3 characters to search messages')}
					</Box>
				</Portal>
			)}
		</Box>
	);
};
